import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, addDays, isWithinInterval, parseISO, differenceInDays } from 'date-fns';
import { 
  Clock, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  Flag, 
  User, 
  ChevronDown, 
  ChevronUp,
  Filter,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Task {
  id: string;
  title: string;
  description?: string;
  due_date: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'completed';
  assignee?: string;
  created_at: string;
  updated_at: string;
}

const UpcomingTasks: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [filterPriority, setFilterPriority] = useState<string>('all');
  
  // Fetch tasks
  const { data: tasks, isLoading, refetch } = useQuery({
    queryKey: ['upcomingTasks'],
    queryFn: async () => {
      const today = new Date();
      const nextWeek = addDays(today, 7);
      
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('status', 'pending')
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      
      // Filter tasks due within the next 7 days
      return (data as Task[]).filter(task => {
        const dueDate = parseISO(task.due_date);
        return isWithinInterval(dueDate, { start: today, end: nextWeek });
      });
    }
  });

  // Filter tasks by priority if needed
  const filteredTasks = tasks?.filter(task => 
    filterPriority === 'all' || task.priority === filterPriority
  );

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in-progress':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get urgency indicator based on due date
  const getUrgencyIndicator = (dueDate: string) => {
    const today = new Date();
    const taskDueDate = parseISO(dueDate);
    const daysRemaining = differenceInDays(taskDueDate, today);
    
    if (daysRemaining < 0) {
      return { color: 'bg-red-500', label: 'Overdue' };
    } else if (daysRemaining === 0) {
      return { color: 'bg-red-500', label: 'Due Today' };
    } else if (daysRemaining === 1) {
      return { color: 'bg-orange-500', label: 'Due Tomorrow' };
    } else if (daysRemaining <= 3) {
      return { color: 'bg-yellow-500', label: `${daysRemaining} Days Left` };
    } else {
      return { color: 'bg-green-500', label: `${daysRemaining} Days Left` };
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div 
        className="flex justify-between items-center cursor-pointer mb-4"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <Calendar className="h-5 w-5 text-primary mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">
            Upcoming Tasks (7-Day View)
          </h2>
          {filteredTasks && filteredTasks.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-sm bg-primary/10 text-primary rounded-full">
              {filteredTasks.length} {filteredTasks.length === 1 ? 'task' : 'tasks'}
            </span>
          )}
        </div>
        <div className="flex items-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              refetch();
            }}
            className="p-1 mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            title="Refresh tasks"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button className="text-gray-400 hover:text-gray-600">
            {isExpanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <>
          <div className="mb-4 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing tasks due in the next 7 days
            </div>
            <div className="flex items-center">
              <Filter className="h-4 w-4 text-gray-400 mr-2" />
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              >
                <option value="all">All Priorities</option>
                <option value="high">High Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="low">Low Priority</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredTasks && filteredTasks.length > 0 ? (
            <div className="space-y-3">
              {filteredTasks.map((task) => {
                const urgency = getUrgencyIndicator(task.due_date);
                
                return (
                  <div 
                    key={task.id} 
                    className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                      urgency.label === 'Overdue' || urgency.label === 'Due Today'
                        ? 'border-red-300 bg-red-50/50'
                        : urgency.label === 'Due Tomorrow'
                        ? 'border-orange-300 bg-orange-50/50'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-gray-900">{task.title}</h3>
                        {task.description && (
                          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          <Flag className="h-3 w-3 mr-1" />
                          {task.priority}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {task.status === 'completed' ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          ) : task.status === 'in-progress' ? (
                            <Clock className="h-3 w-3 mr-1" />
                          ) : (
                            <AlertCircle className="h-3 w-3 mr-1" />
                          )}
                          {task.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center mt-3 text-sm text-gray-500 gap-4">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                        <span>Due: {format(parseISO(task.due_date), 'MMM d, yyyy')}</span>
                      </div>
                      
                      {task.assignee && (
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1 text-gray-400" />
                          <span>{task.assignee}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center ml-auto">
                        <div className={`h-2 w-2 rounded-full ${urgency.color} mr-1`}></div>
                        <span className="text-xs font-medium">{urgency.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p>No tasks due in the next 7 days</p>
              <p className="text-sm text-gray-400 mt-1">
                All caught up! Any new tasks will appear here.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UpcomingTasks;